import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2, Send } from 'lucide-react'
import { sendVerificationEmail, updatePassword, BASE_URL } from '../../services/api'
import '../../styles/AuthLayout.css'

export default function ForgotPassword() {
  const [step, setStep] = useState(1) // 1: Email, 2: OTP, 3: Reset
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [companyId, setCompanyId] = useState(() => {
    // Try to recover from session if user is logged in
    const saved = sessionStorage.getItem('user')
    if (saved) {
      try {
        const u = JSON.parse(saved)
        return u.Companyid || u.company_id || "1"
      } catch (e) { return "1" }
    }
    return "1"
  })
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const handleSendOTP = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const senderName = email.split('@')[0] || "User"
      const data = await sendVerificationEmail(email, senderName, '000000', companyId)
      if (data.success || data.Response) {
        setStep(2)
      } else {
        alert(data.message || 'Failed to send OTP')
      }
    } catch (err) { 
      if (err.message.includes('Failed to fetch')) {
        // Proceed anyway because the email is usually sent but blocked by CORS
        setStep(2)
      } else {
        alert('Error sending OTP: ' + err.message) 
      }
    }
    setLoading(false)
  }

  const handleVerifyOTP = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const response = await fetch(`${BASE_URL}Pages/ForgetPassword.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          Companyid: companyId,
          Email: email,
          OTP: otp
        })
      });

      const data = await response.json();
      
      if (data.success || data.Response === "SUCCESS" || data.status === "success") {
        // Retrieve real Company ID from response if provided by backend
        const capturedCid = data.Companyid || data.company_id || (data.Response && data.Response.Companyid);
        if (capturedCid) {
          setCompanyId(String(capturedCid));
        }
        setStep(3)
      } else {
        alert(data.message || 'Verification failed. Please check the OTP.')
      }
    } catch (err) {
      alert('Error verifying OTP: ' + err.message)
    }
    setLoading(false)
  }

  const handleResetPassword = async (e) => {
    e.preventDefault()
    if (newPassword !== confirmPassword) {
      alert("Passwords don't match")
      return
    }
    const finalCid = localStorage.getItem('reset_cid') || companyId;
    
    if (!finalCid || finalCid === "1" || finalCid === "") {
      console.warn("Retrying discovery: Companyid missing from storage.");
    }

    const payload = { 
      Companyid: finalCid, 
      Email: localStorage.getItem('reset_email') || email, 
      OTP: otp,
      Password: newPassword 
    };

    console.log("Submitting Password Update Payload:", payload);

    try {
      const data = await updatePassword(payload);
      if (data.success || data.Response === "UPDATED" || data.Response === true || data.status === "success") {
        alert('Password reset successfully')
        window.location.href = '/'
      } else {
        alert(data.message || 'Reset failed')
      }
    } catch (err) { 
      alert('Error resetting password: ' + err.message) 
    }
    setLoading(false)
  }

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ maxWidth: '400px' }}>
        <div className="auth-header" style={{ padding: '28px 20px' }}>
          <div className="auth-logo" style={{ fontSize: '36px' }}>🔑</div>
          <h1 className="auth-title" style={{ fontSize: '18px' }}>Petro Genius</h1>
          <p className="auth-subtitle">Password Recovery</p>
        </div>

        <div className="auth-body" style={{ padding: '28px 32px' }}>
          <h2 className="auth-title-dark">Recover Access</h2>
          <p className="auth-subtitle-dark" style={{ marginBottom: '24px' }}>
            {step === 1 && "Enter email for recovery code"}
            {step === 2 && "Enter the 6-digit code"}
            {step === 3 && "Create a new password"}
          </p>

          {step === 1 && (
            <form onSubmit={handleSendOTP} style={{ display: 'grid', gap: '2px' }}>
              <div className="auth-field">
                <label>Registered Email *</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="auth-input" placeholder="example@company.com" required />
              </div>
              <button type="submit" className="auth-btn-primary" style={{ width: '100%' }} disabled={loading}>
                {loading ? <Loader2 className="animate-spin" size={18} /> : 'Send OTP'} <Send size={16} style={{ marginLeft: '8px' }} />
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleVerifyOTP} style={{ display: 'grid', gap: '2px' }}>
              <div className="auth-field">
                <label>Verification Code *</label>
                <input value={otp} onChange={e => setOtp(e.target.value)} className="auth-input" style={{ textAlign: 'center', fontSize: '18px', fontWeight: '800', letterSpacing: '8px' }} placeholder="000000" maxLength="6" required />
              </div>
              <button type="submit" className="auth-btn-primary" style={{ width: '100%' }}>Verify Code</button>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleResetPassword} style={{ display: 'grid', gap: '2px' }}>
              <div className="auth-field">
                <label>New Password *</label>
                <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="auth-input" placeholder="New Password" required />
              </div>
              <div className="auth-field">
                <label>Confirm Password *</label>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="auth-input" placeholder="Repeat Password" required />
              </div>
              <button type="submit" className="auth-btn-primary" style={{ width: '100%' }} disabled={loading}>
                {loading ? <Loader2 className="animate-spin" size={18} /> : 'Reset Password'}
              </button>
            </form>
          )}

          <div style={{ marginTop: '24px', textAlign: 'center' }}>
            <span onClick={() => window.location.href = '/'} style={{ fontSize: '13px', color: '#1a2b3c', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              <ArrowLeft size={16} /> Back to Login
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
